import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import API_URLS from "../../../core/common/apiUrls";
import AirplaneLoader from "../flight-loader/flightLoader";

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface HeldBooking {
  booking_id: number;
  tbo_booking_id: string;
  pnr: string;
  flight_offer_id: number;
  result_index: string;
  final_price: number;
  currency: string;
  hold_until: string | null;
  hold_amount_paid: number;
  remaining_amount: number;
  segments?: any[][];
  airline?: { name?: string; iata_code?: string };
}

interface LeadPassenger {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface HoldData {
  held_bookings: HeldBooking[];
  lead_passenger: LeadPassenger;
}

const FlightHoldConfirmation: React.FC = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();

  const { holdData, totalFare, holdDepositAmount } = (location.state || {}) as {
    holdData: HoldData;
    totalFare: number;
    holdDepositAmount: number;
  };

  const booking = holdData?.held_bookings?.[0];
  const lead = holdData?.lead_passenger;

  const [completing, setCompleting] = useState(false);
  const [issuingTicket, setIssuingTicket] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [notifEmail, setNotifEmail] = useState<string>(lead?.email || "");
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);

  const parseHoldDate = (raw: string | null): Date | null => {
    if (!raw) return null;
    // Handle "dd-mm-yyyy HH:MM" format from legacy API responses
    const legacy = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
    if (legacy) {
      const [, dd, mm, yyyy, hh, min] = legacy;
      return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:00`);
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  // Countdown timer to hold deadline
  useEffect(() => {
    if (!booking?.hold_until) return;
    const parsed = parseHoldDate(booking.hold_until);
    if (!parsed) return;
    const deadline = parsed.getTime();

    const tick = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft("Expired");
        return;
      }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };

    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [booking?.hold_until]);

  const formatDeadline = (raw: string | null) => {
    if (!raw) return "Complete before departure";
    const d = parseHoldDate(raw);
    if (!d) return "Complete before departure";
    return d.toLocaleString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCompleteBooking = async () => {
    if (!booking) return;
    setCompleting(true);

    try {
      const remaining = booking.remaining_amount ?? (totalFare - holdDepositAmount);

      const orderRes = await fetch(API_URLS.PAYMENTS.CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: remaining,
          currency: booking.currency || "INR",
          coupon_code: null,
          discount_amount: 0,
        }),
      });

      const orderData = await orderRes.json();
      const { order_id, razorpay_key_id, amount, currency } = orderData;

      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded. Please refresh and try again.");
        setCompleting(false);
        return;
      }

      const options = {
        key: razorpay_key_id,
        amount: String(amount),
        currency,
        name: "Complete Flight Booking",
        description: `Remaining payment for PNR: ${booking.pnr}`,
        order_id,
        handler: async (razorResponse: RazorpayResponse) => {
          setCompleting(false);
          setIssuingTicket(true);
          try {
            const verifyRes = await fetch(API_URLS.PAYMENTS.VERIFY_PAYMENT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(razorResponse),
            });
            const verifyData = await verifyRes.json();

            if (verifyData.status !== "success") {
              alert("Payment verification failed. Please try again.");
              setIssuingTicket(false);
              return;
            }

            const completeRes = await fetch(API_URLS.FLIGHTS.COMPLETE_HELD_BOOKING, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                booking_id: booking.booking_id,
                payment_id: verifyData.payment_id,
              }),
            });
            const completeData = await completeRes.json();

            if (completeData.code !== 200) {
              alert(completeData.error || "Failed to complete booking. Please contact support.");
              setIssuingTicket(false);
              return;
            }

            navigate(routes.flightBookingConfirmation, {
              state: {
                fromHoldComplete: true,
                completedData: completeData.data,
              },
            });
          } catch (err) {
            console.error("Complete booking error:", err);
            alert("Something went wrong. Please try again.");
            setIssuingTicket(false);
          }
        },
        prefill: {
          name: `${lead?.first_name || ""} ${lead?.last_name || ""}`,
          email: lead?.email || "",
          contact: lead?.phone || "",
        },
        theme: { color: "#3399cc" },
        modal: {
          ondismiss: () => {
            setCompleting(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        alert("Payment failed. Please try again.");
        setCompleting(false);
      });
      rzp.open();
    } catch (err) {
      console.error("Complete booking error:", err);
      alert("Something went wrong. Please try again.");
      setCompleting(false);
    }
  };

  if (!holdData || !booking) {
    return (
      <div>
        <div className="breadcrumb-bar flightList-banner-bg text-center">
          <div className="container">
            <h2 className="breadcrumb-title mb-2">Hold Confirmation</h2>
          </div>
        </div>
        <div className="content">
          <div className="container text-center py-5">
            <i className="isax isax-warning-2 text-warning" style={{ fontSize: 64 }} />
            <h4 className="mt-3">No hold data found</h4>
            <p className="text-muted">Please go back and try again.</p>
            <Link to={routes.home1} className="btn btn-primary mt-3">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const remaining = booking.remaining_amount ?? (totalFare - holdDepositAmount);
  const holdDate = parseHoldDate(booking.hold_until ?? null);
  const isExpired = holdDate ? holdDate < new Date() : false;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb-bar flightList-banner-bg text-center">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h2 className="breadcrumb-title mb-2">Price Locked</h2>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center mb-0">
                  <li className="breadcrumb-item"><Link to={routes.home1}>Home</Link></li>
                  <li className="breadcrumb-item active">Hold Confirmation</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-8">

              {/* ── Success Header ── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center py-5">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 80, height: 80, background: "#fff3cd" }}
                  >
                    <span style={{ fontSize: 38 }}>🔒</span>
                  </div>
                  <h3 className="fw-bold mb-1" style={{ color: "#92400e" }}>Price Locked!</h3>
                  <p className="text-muted mb-0">
                    Your flight price has been locked. Complete payment before the deadline to confirm your ticket.
                  </p>
                </div>
              </div>

              {/* ── Flight Details ── */}
              {booking.segments && booking.segments.length > 0 && (() => {
                const allSegs = booking.segments.flat();
                const first = allSegs[0];
                const last = allSegs[allSegs.length - 1];
                const fromCity = first?.Origin?.Airport?.CityName || first?.Origin?.Airport?.AirportCode || "—";
                const toCity = last?.Destination?.Airport?.CityName || last?.Destination?.Airport?.AirportCode || "—";
                const depTime = first?.Origin?.DepTime
                  ? new Date(first.Origin.DepTime).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                  : "—";
                const airlineName = first?.Airline?.AirlineName || booking.airline?.name || "—";
                const airlineCode = first?.Airline?.AirlineCode || booking.airline?.iata_code || "";
                return (
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-white border-bottom py-3">
                      <h6 className="mb-0 fw-semibold">Flight Details</h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div>
                          <span className="fw-bold fs-15">{airlineName}</span>
                          {airlineCode && <span className="text-muted fs-12 ms-2">{airlineCode}</span>}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 mb-2">
                        <div className="text-center">
                          <div className="fw-bold fs-18">{first?.Origin?.Airport?.AirportCode || fromCity}</div>
                          <div className="text-muted fs-12">{fromCity}</div>
                        </div>
                        <div className="flex-fill text-center">
                          {allSegs.length > 1 && (
                            <div className="text-muted fs-11 mb-1">{allSegs.length - 1} stop{allSegs.length > 2 ? "s" : ""}</div>
                          )}
                          <div style={{ borderTop: "2px dashed #dee2e6", position: "relative" }}>
                            <i className="isax isax-airplane" style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: "#fff", padding: "0 4px", color: "#6c757d", fontSize: 14 }} />
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="fw-bold fs-18">{last?.Destination?.Airport?.AirportCode || toCity}</div>
                          <div className="text-muted fs-12">{toCity}</div>
                        </div>
                      </div>
                      <div className="text-muted fs-13 mt-2">
                        <i className="isax isax-clock me-1" />
                        Departure: <span className="fw-semibold text-dark">{depTime}</span>
                      </div>
                      {allSegs.length > 1 && (
                        <div className="mt-3 pt-2 border-top">
                          {allSegs.map((seg: any, i: number) => (
                            <div key={i} className="d-flex align-items-center gap-2 fs-12 text-muted mb-1">
                              <span className="badge bg-light text-dark border">{seg?.Airline?.AirlineCode} {seg?.Airline?.FlightNumber}</span>
                              <span>{seg?.Origin?.Airport?.CityName || seg?.Origin?.Airport?.AirportCode}</span>
                              <i className="isax isax-arrow-right-1" style={{ fontSize: 10 }} />
                              <span>{seg?.Destination?.Airport?.CityName || seg?.Destination?.Airport?.AirportCode}</span>
                              <span className="ms-auto">{seg?.Origin?.DepTime ? new Date(seg.Origin.DepTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── PNR + Deadline ── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-sm-6 text-center text-sm-start">
                      <p className="text-muted fs-13 mb-1">Your PNR</p>
                      <h2 className="fw-bold text-primary mb-0" style={{ letterSpacing: 3 }}>
                        {booking.pnr}
                      </h2>
                    </div>
                    <div className="col-sm-6 text-center text-sm-end">
                      <p className="text-muted fs-13 mb-1">Hold Deadline</p>
                      <p className="fw-semibold mb-0 fs-15">{formatDeadline(booking.hold_until)}</p>
                      {!isExpired && timeLeft && (
                        <p className="mb-0 mt-1">
                          <span
                            className="badge fs-13 fw-semibold px-3 py-1"
                            style={{ background: "#fff3cd", color: "#92400e" }}
                          >
                            ⏳ {timeLeft} remaining
                          </span>
                        </p>
                      )}
                      {isExpired && (
                        <span className="badge bg-danger fs-13 px-3 py-1 mt-1">Expired</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Payment Summary ── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0 fw-semibold">Payment Summary</h6>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-muted">Total Fare</span>
                      <span className="fw-semibold">
                        ₹{(booking.final_price ?? totalFare ?? 0).toLocaleString()}
                      </span>
                    </li>
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-muted d-flex align-items-center gap-2">
                        Deposit Paid
                        <span className="badge bg-success fs-11">✓ Paid</span>
                      </span>
                      <span className="text-success fw-semibold">
                        − ₹{(booking.hold_amount_paid ?? holdDepositAmount ?? 0).toLocaleString()}
                      </span>
                    </li>
                    <li className="d-flex justify-content-between py-2">
                      <span className="fw-semibold fs-15">Remaining Due</span>
                      <span className="fw-bold fs-18 text-primary">
                        ₹{remaining.toLocaleString()}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* ── Important to Know ── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0 fw-semibold">Important to Know</h6>
                </div>
                <div className="card-body p-0">
                  <div className="d-flex gap-3 p-3" style={{ background: "#fef2f2" }}>
                    <div style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.4 }}>🚫</div>
                    <div>
                      <p className="fw-semibold mb-1 fs-13" style={{ color: "#991b1b" }}>Non-Refundable Deposit</p>
                      <p className="mb-0 fs-12 text-muted">Cost of Price Lock is <strong>non-refundable</strong> &amp; non-adjustable against the flight booking.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Lead Passenger ── */}
              {lead && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white border-bottom py-3">
                    <h6 className="mb-0 fw-semibold">Lead Passenger</h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <p className="text-muted fs-13 mb-1">Name</p>
                        <p className="fw-semibold mb-0">{lead.first_name} {lead.last_name}</p>
                      </div>
                      <div className="col-sm-6">
                        <p className="text-muted fs-13 mb-1">Email</p>
                        <p className="fw-semibold mb-0">{lead.email}</p>
                      </div>
                      <div className="col-sm-6">
                        <p className="text-muted fs-13 mb-1">Phone</p>
                        <p className="fw-semibold mb-0">{lead.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Important Notice ── */}
              <div
                className="alert border-0 mb-4 d-flex gap-3"
                style={{ background: "#fffbeb", borderLeft: "4px solid #f59e0b" } as React.CSSProperties}
              >
                <span style={{ fontSize: 22 }}>⚠️</span>
                <div>
                  <p className="fw-semibold mb-1" style={{ color: "#92400e" }}>Important</p>
                  <ul className="mb-0 ps-3 fs-13 text-muted">
                    <li>Your ticket is <strong>not confirmed</strong> until you complete the remaining payment.</li>
                    <li>We will <strong>cancel your booking</strong> if payment is not completed before the deadline.</li>
                    <li>Fares may change if you search again after expiry.</li>
                    <li>
                      Booking confirmation sent to{" "}
                      {editingEmail ? (
                        <span className="d-inline-flex align-items-center gap-1 ms-1">
                          <input
                            type="email"
                            className="form-control form-control-sm d-inline-block"
                            style={{ width: 200 }}
                            value={notifEmail}
                            onChange={e => { setNotifEmail(e.target.value); setEmailSaved(false); }}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-success px-2 py-0"
                            onClick={() => { setEditingEmail(false); setEmailSaved(true); }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary px-2 py-0"
                            onClick={() => { setNotifEmail(lead?.email || ""); setEditingEmail(false); setEmailSaved(false); }}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <span className="ms-1">
                          <strong>{notifEmail || lead?.email || "your email"}</strong>
                          {emailSaved && <span className="text-success ms-1 fs-11">✓ Updated</span>}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-1 fs-11"
                            onClick={() => setEditingEmail(true)}
                          >
                            Edit
                          </button>
                        </span>
                      )}
                    </li>
                  </ul>
                </div>
              </div>

              {/* ── Actions ── */}
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  {isExpired ? (
                    <div className="text-center">
                      <p className="text-danger fw-semibold mb-3">
                        Your hold has expired. Please search for a new flight.
                      </p>
                      <Link to={routes.home1} className="btn btn-primary px-5">
                        Search Flights
                      </Link>
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center gap-3">
                      <button
                        className="btn btn-primary w-100 fw-semibold"
                        style={{ padding: "14px 24px", fontSize: 16 }}
                        onClick={handleCompleteBooking}
                        disabled={completing || issuingTicket}
                      >
                        {completing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Opening payment...
                          </>
                        ) : (
                          `Complete Booking — Pay ₹${remaining.toLocaleString()}`
                        )}
                      </button>
                      <div className="d-flex gap-3 w-100">
                        <Link
                          to={routes.home1}
                          className="btn btn-outline-secondary flex-fill text-center"
                        >
                          Search Another Flight
                        </Link>
                        <Link
                          to={routes.home1}
                          className="btn btn-outline-secondary flex-fill text-center"
                        >
                          Back to Home
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {issuingTicket && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <AirplaneLoader text="Issuing your ticket..." size="large" />
        </div>
      )}
    </div>
  );
};

export default FlightHoldConfirmation;
